import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '@/hooks/useConfig';
import ConfettiExplosion from '@/components/shared/ConfettiExplosion';
import type { ContentBlocks } from '@crypto-factory/shared-types';

export default function HeroSection() {
    const { config } = useConfig();
    const [showConfetti, setShowConfetti] = useState(false);
    const [copied, setCopied] = useState(false);

    const content = (config.content || {}) as Partial<ContentBlocks>;
    const headline = content.heroHeadline || 'Alpha Calls Only';
    const subtitle = content.heroSubtitle || '100x or nothing. Join the degen elite.';
    const ctaText = content.ctaText || 'APE IN NOW';
    const walletAddress = content.walletAddress || '0x...';

    const handleCopyCA = useCallback(() => {
        navigator.clipboard.writeText(walletAddress);
        setCopied(true);
        setShowConfetti(true);
        setTimeout(() => setCopied(false), 2000);
        setTimeout(() => setShowConfetti(false), 5000);
    }, [walletAddress]);

    return (
        <section className="relative min-h-[90vh] flex items-center justify-center px-4">
            {/* Confetti */}
            {showConfetti && <ConfettiExplosion />}

            <div className="text-center max-w-4xl mx-auto relative z-10">
                {/* Glitch headline */}
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                    className="font-display text-5xl md:text-7xl lg:text-8xl font-bold mb-6 relative"
                >
                    <span className="relative inline-block group">
                        <span
                            className="neon-glow text-neon-green"
                            style={{ textShadow: '0 0 20px #00FF00, 0 0 40px #00FF00' }}
                        >
                            {headline}
                        </span>

                        {/* Glitch layers */}
                        <span
                            className="absolute inset-0 text-neon-pink opacity-0 group-hover:opacity-70 group-hover:animate-glitch"
                            style={{ clipPath: 'inset(40% 0 0 0)' }}
                        >
                            {headline}
                        </span>
                        <span
                            className="absolute inset-0 text-neon-cyan opacity-0 group-hover:opacity-70 group-hover:animate-glitch"
                            style={{ clipPath: 'inset(0 0 60% 0)', animationDelay: '0.1s' }}
                        >
                            {headline}
                        </span>
                    </span>
                </motion.h1>

                {/* Subtitle */}
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="text-zinc-400 text-lg md:text-xl mb-10 font-body max-w-2xl mx-auto"
                >
                    {subtitle}
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                >
                    {/* Primary CTA */}
                    <button
                        onClick={handleCopyCA}
                        className="btn-primary text-lg animate-glow-pulse"
                    >
                        {copied ? '✓ COPIED!' : ctaText}
                    </button>

                    {/* Secondary CTA */}
                    <button className="btn-ghost flex items-center gap-2">
                        <span>🔗</span>
                        <span>Join Telegram</span>
                    </button>
                </motion.div>

                {/* Contract Address */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                    className="mt-8"
                >
                    <p className="text-zinc-600 text-sm font-mono">
                        CA: <span className="text-zinc-400">{walletAddress}</span>
                    </p>
                </motion.div>

                {/* Stats or social proof */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9, duration: 0.6 }}
                    className="mt-12 flex justify-center gap-8 text-center"
                >
                    {[
                        { label: 'MEMBERS', value: '10K+' },
                        { label: 'WIN RATE', value: '87%' },
                        { label: 'AVG RETURN', value: '42X' },
                    ].map((stat) => (
                        <div key={stat.label} className="glass px-6 py-4 rounded-xl">
                            <p className="text-2xl md:text-3xl font-bold text-neon-green font-display">
                                {stat.value}
                            </p>
                            <p className="text-xs text-zinc-500 mt-1">{stat.label}</p>
                        </div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
}
